import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { HealthBar } from '../../HealthBar.js';
import { PALETTE, matte, colored } from '../../Style.js';

const TUNIC = 0x5d6b3a;
const LEATHER = 0x6b4a2e;
const BOOT = PALETTE.woodDark;
const SKIN = 0xd9a77e;
const SHADOW = 0x2a2018;
const FLETCH = 0xeae3d2;

// The bow arm is held slightly forward, gripping the longbow close to the body.
// Everything stays inside the server's 0.8 collision radius so nothing pokes into walls.
const BOW_POSE = -0.4;
const CLOAK_REST = 0.06;

// Widen the bottom of a geometry so cloth flares out as it hangs
function flare(geometry, amount) {
  const pos = geometry.attributes.position;
  geometry.computeBoundingBox();
  const { min, max } = geometry.boundingBox;
  for (let i = 0; i < pos.count; i++) {
    const t = (max.y - pos.getY(i)) / (max.y - min.y);
    pos.setX(i, pos.getX(i) * (1 + amount * t));
  }
  return geometry;
}

// A tall curved longbow, built as a chain of short segments bowing forward
function longbow(height, bulge) {
  const parts = [];
  const segments = 6;
  const point = t => [t * height / 2, bulge * (1 - t * t)];
  for (let i = 0; i < segments; i++) {
    const [y0, z0] = point(-1 + (2 * i) / segments);
    const [y1, z1] = point(-1 + (2 * (i + 1)) / segments);
    const length = Math.hypot(y1 - y0, z1 - z0);
    parts.push(colored(new THREE.BoxGeometry(0.07, length + 0.02, 0.09), PALETTE.woodLight, {
      y: (y0 + y1) / 2, z: (z0 + z1) / 2, rx: Math.atan2(z1 - z0, y1 - y0)
    }));
  }
  parts.push(colored(new THREE.BoxGeometry(0.1, 0.28, 0.13), LEATHER, { z: bulge }));
  parts.push(colored(new THREE.BoxGeometry(0.02, height, 0.02), PALETTE.cloth, {}));
  return parts;
}

// Longbow ranger: hooded team cloak, quiver of arrows, a bow nearly as tall as they are
export class Sniper {
  constructor(scene, unit, color) {
    this.scene = scene;
    this.unit = unit;
    this.color = color;
    this.mesh = null;
    this.lastPosition = null;
    this.targetRotation = 0;
    this.healthBar = null;
    this.maxHealth = 15; // SniperHealth from server
    this.geometries = [];
    this.materials = [];
    this.walk = 0;
    this.walkPhase = 0;
    this.lastAnimTime = null;
    this.lastMeshPosition = new THREE.Vector3();
    this.create();
  }

  // Merge coloured parts into a single mesh using this ranger's material
  part(parts) {
    const geometry = mergeGeometries(parts);
    parts.forEach(p => p.dispose());
    this.geometries.push(geometry);
    const mesh = new THREE.Mesh(geometry, this.material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }

  // A pivoting limb attached to the body
  limb(parts, x, y, z) {
    const pivot = new THREE.Group();
    pivot.position.set(x, y, z);
    pivot.add(this.part(parts));
    this.body.add(pivot);
    return pivot;
  }

  create() {
    const bodyGroup = new THREE.Group();

    this.material = matte(0xffffff, { vertexColors: true, unique: true });
    this.materials.push(this.material);

    const team = new THREE.Color(this.color);
    const teamDark = team.clone().multiplyScalar(0.65);

    // Everything that bobs while walking hangs off the body
    this.body = new THREE.Group();
    bodyGroup.add(this.body);

    // Longbow gripped in the left hand, standing upright at the ranger's side
    const bow = longbow(2.2, 0.16);
    const bowGeometry = mergeGeometries(bow);
    bow.forEach(p => p.dispose());
    bowGeometry.translate(-0.5, 1.1, 0.1);

    // Tunic and belt, team cloak over the shoulders, hood with a shadowed face
    this.body.add(this.part([
      colored(new THREE.BoxGeometry(0.84, 0.95, 0.52), TUNIC, { y: 1.25 }),
      colored(flare(new THREE.BoxGeometry(0.8, 0.3, 0.5), 0.2), TUNIC, { y: 0.72 }),
      colored(new THREE.BoxGeometry(0.88, 0.12, 0.56), LEATHER, { y: 1.0 }),
      colored(new THREE.BoxGeometry(0.12, 0.12, 0.04), 0xc8a24a, { y: 1.0, z: 0.29 }),
      colored(new THREE.CylinderGeometry(0.4, 0.6, 0.48, 8), team, { y: 1.62 }),
      colored(new THREE.BoxGeometry(0.06, 0.4, 0.06), LEATHER, { x: 0.14, y: 1.3, z: 0.28, rz: -0.6 }),
      colored(new THREE.SphereGeometry(0.36, 8, 6), team, { y: 2.04, z: -0.06, scale: [1, 1.05, 1.1] }),
      colored(new THREE.ConeGeometry(0.2, 0.42, 6), teamDark, { y: 2.2, z: -0.32, rx: -1.0 }),
      colored(new THREE.BoxGeometry(0.42, 0.34, 0.12), SHADOW, { y: 2.0, z: 0.3 }),
      colored(new THREE.BoxGeometry(0.3, 0.2, 0.08), SKIN, { y: 1.96, z: 0.34 }),
      colored(new THREE.BoxGeometry(0.08, 0.1, 0.08), SKIN, { y: 1.95, z: 0.4 }),
      bowGeometry
    ]));

    // Legs: trousers and soft boots, pivoting at the hip
    const leg = () => [
      colored(new THREE.BoxGeometry(0.3, 0.5, 0.32), LEATHER, { y: -0.25 }),
      colored(new THREE.BoxGeometry(0.34, 0.32, 0.44), BOOT, { y: -0.64, z: 0.05 })
    ];
    this.leftLeg = this.limb(leg(), -0.22, 0.8, 0);
    this.rightLeg = this.limb(leg(), 0.22, 0.8, 0);

    // Arms: tunic sleeve and leather bracer
    const arm = () => [
      colored(new THREE.BoxGeometry(0.24, 0.5, 0.26), TUNIC, { y: -0.27 }),
      colored(new THREE.BoxGeometry(0.26, 0.2, 0.28), LEATHER, { y: -0.56 }),
      colored(new THREE.BoxGeometry(0.2, 0.14, 0.2), SKIN, { y: -0.72 })
    ];
    this.leftArm = this.limb(arm(), -0.48, 1.62, 0);
    this.leftArm.rotation.x = BOW_POSE;
    this.rightArm = this.limb(arm(), 0.48, 1.62, 0);

    // Long back cloak in team colour, with the quiver strapped over it so both sway together
    const arrows = [];
    for (let i = 0; i < 4; i++) {
      const ax = -0.08 + (i % 2) * 0.12;
      const az = -0.06 + Math.floor(i / 2) * 0.1;
      arrows.push(colored(new THREE.BoxGeometry(0.03, 0.4, 0.03), PALETTE.woodLight, { x: ax, y: 0.48, z: az }));
      arrows.push(colored(new THREE.BoxGeometry(0.1, 0.14, 0.02), i % 2 ? FLETCH : team, { x: ax, y: 0.62, z: az }));
    }
    const quiver = mergeGeometries([
      colored(new THREE.CylinderGeometry(0.15, 0.12, 0.72, 6), LEATHER, {}),
      colored(new THREE.CylinderGeometry(0.16, 0.16, 0.08, 6), BOOT, { y: 0.3 }),
      ...arrows
    ]);
    quiver.rotateZ(-0.35);
    quiver.translate(0.12, -0.32, -0.17);
    this.cloak = this.limb([
      colored(flare(new THREE.BoxGeometry(0.8, 1.25, 0.07), 0.15), team, { y: -0.6 }),
      quiver
    ], 0, 1.76, -0.28);
    this.cloak.rotation.x = CLOAK_REST;

    // Create health bar
    this.healthBar = new HealthBar(this.maxHealth, 1.6, 0.25);
    this.healthBar.getGroup().position.y = 2.56;
    bodyGroup.add(this.healthBar.getGroup());

    this.mesh = bodyGroup;

    // Set initial position
    this.mesh.position.set(this.unit.position.x, this.unit.position.y, this.unit.position.z);
    this.lastPosition = { ...this.unit.position };
    this.lastMeshPosition.copy(this.mesh.position);

    this.scene.add(this.mesh);
  }

  updatePosition(position) {
    if (this.mesh) {
      // Calculate rotation based on movement direction
      if (this.lastPosition) {
        const dx = position.x - this.lastPosition.x;
        const dz = position.z - this.lastPosition.z;
        const distanceMoved = Math.sqrt(dx * dx + dz * dz);

        // Only update rotation if we've moved significantly
        if (distanceMoved > 0.01) {
          this.targetRotation = Math.atan2(dx, dz);
        }
      }

      // Smoothly interpolate rotation
      let rotationDiff = this.targetRotation - this.mesh.rotation.y;
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
      this.mesh.rotation.y += rotationDiff * 0.15;

      // Store last position
      this.lastPosition = { x: position.x, y: position.y, z: position.z };

      this.animate();
    }
  }

  // Walk cycle driven by how far the interpolated mesh moved since the last frame
  animate() {
    const now = performance.now() / 1000;
    const dt = this.lastAnimTime === null ? 0 : Math.min(now - this.lastAnimTime, 0.1);
    this.lastAnimTime = now;
    if (dt <= 0) return;

    const pos = this.mesh.position;
    const moved = Math.min(Math.hypot(pos.x - this.lastMeshPosition.x, pos.z - this.lastMeshPosition.z), 1);
    this.lastMeshPosition.copy(pos);

    const target = Math.min(1, moved / dt / 2.5);
    this.walk += (target - this.walk) * Math.min(1, dt * 8);
    this.walkPhase += moved * 3.2;

    const swing = Math.sin(this.walkPhase) * 0.5 * this.walk;
    this.leftLeg.rotation.x = swing;
    this.rightLeg.rotation.x = -swing;
    this.rightArm.rotation.x = swing * 0.8;
    this.leftArm.rotation.x = BOW_POSE + Math.sin(this.walkPhase * 2) * 0.04 * this.walk;
    // Rise as the legs spread so the swinging feet stay on the ground
    this.body.position.y = Math.abs(Math.sin(this.walkPhase)) * 0.04 * this.walk;
    this.cloak.rotation.x = CLOAK_REST + this.walk * 0.12 + Math.sin(now * 1.7) * 0.02 + Math.sin(this.walkPhase * 2) * 0.03 * this.walk;
  }

  // Set target position for aiming
  setTarget(targetPosition) {
    if (this.mesh && targetPosition) {
      const dx = targetPosition.x - this.mesh.position.x;
      const dz = targetPosition.z - this.mesh.position.z;
      this.targetRotation = Math.atan2(dx, dz);
    }
  }

  // Update health bar
  updateHealth(health, camera) {
    if (this.healthBar) {
      this.healthBar.update(health, camera);
    }
  }

  remove() {
    if (this.healthBar) {
      this.healthBar.dispose();
    }
    this.geometries.forEach(g => g.dispose());
    this.materials.forEach(m => m.dispose());
    this.geometries = [];
    this.materials = [];
    if (this.mesh) {
      this.scene.remove(this.mesh);
    }
  }
}
